/**
 * surgeVeins.ts — BETA 23.54 · THE CORRUPTION CRACKS SYSTEM
 * (23.26 Surge veins, rebuilt to Dylan's purple-corruption-cracks pack)
 *
 * The pack's law: "sharp crack core + soft separate glow. Wrong: blurred
 * purple spaghetti pasted on top of floor."
 *
 *  - corruptionInfluence(x,y,seed) 0..1 — the world is infected everywhere,
 *    worse near seeded epicenters (the future Corruption Zone plugs in here)
 *  - FIVE families by influence: hairline → forked → web → rift → crystal
 *  - anti-repetition: every cluster varies seed/branching/width/gaps/color
 *    temperature/nodes/scale; placement prefers path seams, never a grid
 *  - BAKED (chunk ground): blackened dirt + debris integration, recessed
 *    dark fracture (SW shadow / NE lip), then the CRISP NEON CORE — sharp,
 *    steady, never blurred
 *  - LIVE (screen pass): soft aura + branch-node radial lights only, alpha
 *    pulsing with the 4 eerie personalities (23.27) — the glow breathes,
 *    the core does not
 *
 * Walkable: pure detail, no collision.
 */

import { CHUNK_WU, cellRng } from './mapChunks';
import { distToNearestPath } from './paths';       // runtime-only — cycle-safe (same pattern as forest.ts)
import { forestClearance } from './forest';        // runtime-only — cycle-safe

export type CrackFamily = 'hairline' | 'forked' | 'web' | 'rift' | 'crystal';

export interface Vein {
  /** polyline points in world coords (pen-up = null; gaps/breaks included) */
  pts: Array<{ x: number; y: number } | null>;
  minX: number; minY: number; maxX: number; maxY: number;
  phase: number;
  key: string;
  family: CrackFamily;
  /** corruption influence at the site (drives glow strength + bake weight) */
  intensity: number;
  coreW: number;
  glowR: number;
  /** 0..1 color temperature — violet ↔ hot magenta (anti-repetition) */
  warm: number;
  /** branch hubs / line ends — bright nodes + radial lights */
  nodes: Array<{ x: number; y: number; r: number }>;
  /** crystal family only: ground-embedded shard facets */
  shards: Array<{ cx: number; cy: number; pts: Array<{ x: number; y: number }> }>;
}

// ── corruption influence field ───────────────────────────────────────────────
// Epicenters on a seeded 16384wu lattice (the strongest one becomes the REAL
// Corruption Zone in G4 — this field is the API it will plug into), plus a
// fine vein-noise so the infection creeps unevenly, plus a small base so
// hairline cracks exist even in "clean" land.
const EPI_CELL = 16384;
const NOISE_CELL = 700;

function _h32(a: number, b: number, seed: number): number {
  let h = (Math.imul(a | 0, 0x9E3779B1) ^ Math.imul(b | 0, 0x85EBCA6B) ^ Math.imul(seed | 0, 0xC2B2AE35)) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 0x27D4EB2F) >>> 0;
  return ((h ^ (h >>> 15)) >>> 0) / 0xFFFFFFFF;
}
function _smooth(t: number): number { return t * t * (3 - 2 * t); }
function _vnoise(x: number, y: number, seed: number): number {
  const gx = Math.floor(x / NOISE_CELL), gy = Math.floor(y / NOISE_CELL);
  const fx = _smooth(x / NOISE_CELL - gx), fy = _smooth(y / NOISE_CELL - gy);
  const a = _h32(gx, gy, seed), b = _h32(gx + 1, gy, seed);
  const c = _h32(gx, gy + 1, seed), d = _h32(gx + 1, gy + 1, seed);
  return (a * (1 - fx) + b * fx) * (1 - fy) + (c * (1 - fx) + d * fx) * fy;
}

export function corruptionInfluence(x: number, y: number, seed: number): number {
  let zone = 0;
  const ecx = Math.floor(x / EPI_CELL), ecy = Math.floor(y / EPI_CELL);
  for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
    const cx = ecx + dx, cy = ecy + dy;
    const rng = cellRng(cx, cy, seed, 4242);
    if (rng() > 0.38) continue;
    const ex = (cx + 0.2 + rng() * 0.6) * EPI_CELL;
    const ey = (cy + 0.2 + rng() * 0.6) * EPI_CELL;
    const R = 6500 + rng() * 4500;
    const strength = 0.75 + rng() * 0.25;
    const d = Math.hypot(x - ex, y - ey);
    if (d < R) zone = Math.max(zone, strength * _smooth(1 - d / R));
  }
  const vein = _vnoise(x, y, seed ^ 0x51CE);
  return Math.min(1, zone * 0.7 + vein * 0.28 + 0.06);
}

// ── cluster generation ───────────────────────────────────────────────────────
const _veinCache = new Map<string, Vein[]>();

interface FamilyParams {
  trunkSegs: [number, number]; step: [number, number];
  branches: [number, number]; coreW: [number, number];
  glowR: [number, number]; gapChance: number; nodeR: [number, number];
}
const FAMILY: Record<CrackFamily, FamilyParams> = {
  hairline: { trunkSegs: [4, 8],   step: [7, 13],  branches: [0, 1], coreW: [1.1, 1.6], glowR: [3, 5],   gapChance: 0.10, nodeR: [0, 0] },
  forked:   { trunkSegs: [7, 13],  step: [9, 18],  branches: [1, 3], coreW: [1.8, 2.8], glowR: [4.5, 7], gapChance: 0.07, nodeR: [7, 13] },
  web:      { trunkSegs: [4, 7],   step: [8, 14],  branches: [4, 7], coreW: [1.5, 2.3], glowR: [5, 8],   gapChance: 0.05, nodeR: [9, 16] },
  rift:     { trunkSegs: [11, 18], step: [14, 24], branches: [1, 3], coreW: [3.2, 4.8], glowR: [8, 13],  gapChance: 0.13, nodeR: [11, 20] },
  crystal:  { trunkSegs: [8, 13],  step: [11, 18], branches: [2, 4], coreW: [2.6, 3.8], glowR: [9, 14],  gapChance: 0.06, nodeR: [12, 20] },
};

function _pickFamily(inf: number, roll: number): CrackFamily {
  if (inf < 0.22) return roll < 0.85 ? 'hairline' : 'forked';
  if (inf < 0.45) return roll < 0.60 ? 'forked' : roll < 0.80 ? 'hairline' : 'web';
  if (inf < 0.65) return roll < 0.40 ? 'web' : roll < 0.75 ? 'forked' : 'rift';
  if (inf < 0.82) return roll < 0.50 ? 'rift' : roll < 0.80 ? 'web' : 'crystal';
  return roll < 0.45 ? 'crystal' : roll < 0.85 ? 'rift' : 'web';
}

function _lerp(a: number, b: number, t: number): number { return a + (b - a) * t; }
function _range(rng: () => number, r: [number, number]): number { return _lerp(r[0], r[1], rng()); }

function _genVein(rng: () => number, sx: number, sy: number, key: string, family: CrackFamily, inf: number): Vein {
  const P = FAMILY[family];
  const pts: Array<{ x: number; y: number } | null> = [];
  const nodes: Vein['nodes'] = [];
  const shards: Vein['shards'] = [];
  let minX = sx, minY = sy, maxX = sx, maxY = sy;
  const push = (p: { x: number; y: number } | null) => {
    pts.push(p);
    if (p) {
      if (p.x < minX) minX = p.x; if (p.x > maxX) maxX = p.x;
      if (p.y < minY) minY = p.y; if (p.y > maxY) maxY = p.y;
    }
  };
  const walk = (x: number, y: number, ang: number, segs: number, step: number) => {
    push({ x, y });
    for (let i = 0; i < segs; i++) {
      ang += (rng() - 0.5) * 2.35;   // cracks fracture, they don't meander
      const d = step * (0.7 + rng() * 0.6);
      x += Math.cos(ang) * d;
      y += Math.sin(ang) * d * 0.5;   // ground squash — cracks lie flat
      if (rng() < P.gapChance) { push(null); push({ x, y }); }   // breaks — the ground re-knits
      else push({ x, y });
    }
    return { x, y, ang };
  };
  const ang0 = rng() * Math.PI * 2;
  const trunkSegs = Math.round(_range(rng, P.trunkSegs));
  const step = _range(rng, P.step);
  const end = walk(sx, sy, ang0, trunkSegs, step);
  const trunkPts = pts.filter((p): p is { x: number; y: number } => !!p);
  const nB = Math.round(_range(rng, P.branches));
  for (let b = 0; b < nB; b++) {
    const at = family === 'web'
      ? { x: sx, y: sy }   // web: everything radiates from the hub
      : trunkPts[1 + Math.floor(rng() * Math.max(1, trunkPts.length - 2))];
    if (!at) continue;
    push(null);
    const bAng = family === 'web'
      ? rng() * Math.PI * 2
      : ang0 + (rng() < 0.5 ? 1 : -1) * (0.7 + rng() * 0.9);
    walk(at.x, at.y, bAng, Math.max(2, Math.round(trunkSegs * (0.3 + rng() * 0.4))), step * 0.7);
    if (P.nodeR[1] > 0) nodes.push({ x: at.x, y: at.y, r: _range(rng, P.nodeR) });
  }
  if (family === 'web' || family === 'rift' || family === 'crystal') {
    nodes.push({ x: sx, y: sy, r: _range(rng, P.nodeR) * 1.25 });   // the hub burns brightest
    if (rng() < 0.6) nodes.push({ x: end.x, y: end.y, r: _range(rng, P.nodeR) * 0.8 });
  }
  if (family === 'crystal') {
    // ground-embedded shard facets at 2-4 nodes (standing crystal PROPS come
    // with the Corruption Zone biome — these are the floor-level splinters)
    const nSh = 2 + Math.floor(rng() * 3);
    for (let s = 0; s < nSh && nodes.length; s++) {
      const n = nodes[Math.floor(rng() * nodes.length)];
      const facets: Array<{ x: number; y: number }> = [];
      const fN = 4 + Math.floor(rng() * 3);
      const baseA = rng() * Math.PI * 2;
      const rr = 6 + rng() * 12;
      for (let f = 0; f < fN; f++) {
        const a = baseA + (f / fN) * Math.PI * 2 + (rng() - 0.5) * 0.5;
        const r = rr * (0.5 + rng() * 0.8);
        facets.push({ x: n.x + Math.cos(a) * r, y: n.y + Math.sin(a) * r * 0.5 });
      }
      shards.push({ cx: n.x, cy: n.y, pts: facets });
      for (const f of facets) { push(null); push(f); pts.pop(); pts.pop(); if (f.x < minX) minX = f.x; if (f.x > maxX) maxX = f.x; if (f.y < minY) minY = f.y; if (f.y > maxY) maxY = f.y; }
    }
  }
  return {
    pts, minX, minY, maxX, maxY, phase: rng() * Math.PI * 2, key, family,
    intensity: Math.min(1, inf + (rng() - 0.5) * 0.12),
    coreW: _range(rng, P.coreW),
    glowR: _range(rng, P.glowR),
    warm: rng(),
    nodes, shards,
  };
}

/**
 * G8 (dynamic cracks) — generate one vein AT a world point, family picked by
 * local corruption influence exactly like the baked clusters. `salt` keeps
 * repeat cracks near the same spot from being clones (anti-repetition law).
 * Emergence cracks get a small influence floor so even clean-land cracks
 * read as more than a hairline whisper — a creature just tore through here.
 */
export function genVeinAt(x: number, y: number, seed: number, salt: number): Vein {
  const rng = cellRng(Math.round(x), Math.round(y), seed, 0x6C8 ^ Math.imul(salt, 0x9E3779B1));
  const inf = Math.max(0.3, corruptionInfluence(x, y, seed));
  return _genVein(rng, x, y, `dyn:${Math.round(x)},${Math.round(y)}#${salt}`, _pickFamily(inf, rng()), inf);
}

export function veinsForChunk(cx: number, cy: number, seed: number): Vein[] {
  const key = `${cx},${cy},${seed}`;
  const hit = _veinCache.get(key);
  if (hit) return hit;
  if (_veinCache.size > 256) _veinCache.clear();
  const rng = cellRng(cx, cy, seed, 6060);
  const out: Vein[] = [];
  const chunkInf = corruptionInfluence(cx * CHUNK_WU + CHUNK_WU / 2, cy * CHUNK_WU + CHUNK_WU / 2, seed);
  // infection density scales with influence: ~0.5/chunk in clean land,
  // ~3.5/chunk at an epicenter — clusters, never an even grid
  const expected = 0.35 + chunkInf * 3.4;
  let n = Math.floor(expected);
  if (rng() < expected - n) n++;
  for (let i = 0; i < n; i++) {
    // up to 4 candidate sites: skip deep-forest interiors (baked canopy hides
    // the ground — a crack there is wasted), prefer path seams (believable
    // fracture lines follow the built world). Forest chunks would otherwise
    // lose ~80% of their clusters to blind luck — the retries put the cracks
    // ON the corridors where the player actually walks.
    let sx = 0, sy = 0, found = false;
    for (let att = 0; att < 4; att++) {
      const ax = cx * CHUNK_WU + 120 + rng() * (CHUNK_WU - 240);
      const ay = cy * CHUNK_WU + 120 + rng() * (CHUNK_WU - 240);
      if (forestClearance(ax, ay, seed) < -80) continue;
      if (!found || (rng() < 0.6 && distToNearestPath(ax, ay, seed) < distToNearestPath(sx, sy, seed))) {
        sx = ax; sy = ay;
      }
      found = true;
    }
    if (!found) continue;
    const inf = corruptionInfluence(sx, sy, seed);
    out.push(_genVein(rng, sx, sy, `${key}#${i}`, _pickFamily(inf, rng()), inf));
  }
  _veinCache.set(key, out);
  return out;
}

// ── baked layers (chunk ground) ──────────────────────────────────────────────
/**
 * Draw ONE vein's full ground stack (dirt integration + recessed fracture +
 * CRISP NEON CORE + crystal shards) through the given world→px mapping.
 * Shared by the chunk bake AND the G8 dynamic-crack sprite builder — one
 * visual language, one code path (the pack's law lives here only).
 */
export function drawVeinBase(
  c: CanvasRenderingContext2D, v: Vein, seed: number,
  lx: (wx: number) => number, ly: (wy: number) => number,
): void {
  const trace = (offX: number, offY: number, color: string, width: number, alpha = 1) => {
    c.save();
    c.globalAlpha = alpha;
    c.strokeStyle = color;
    c.lineWidth = width;
    c.lineCap = 'round';
    c.lineJoin = 'round';
    c.beginPath();
    let up = true;
    for (const p of v.pts) {
      if (!p) { up = true; continue; }
      if (up) { c.moveTo(lx(p.x) + offX, ly(p.y) + offY); up = false; }
      else c.lineTo(lx(p.x) + offX, ly(p.y) + offY);
    }
    c.stroke();
    c.restore();
  };
  // ── ground integration (UNDER the fracture): blackened dirt blobs +
  // pale debris chips near the big families — the crack belongs to the
  // ground, it isn't pasted on it ──
  const irng = cellRng(Math.round(v.minX), Math.round(v.minY), seed, 7272);
  const solid = v.pts.filter((p): p is { x: number; y: number } => !!p);
  const nDirt = 3 + Math.floor(irng() * 5) + (v.family === 'rift' || v.family === 'crystal' ? 4 : 0);
  c.save();
  for (let i = 0; i < nDirt && solid.length; i++) {
    const p = solid[Math.floor(irng() * solid.length)];
    const r = (3 + irng() * 7) * (v.family === 'rift' ? 1.6 : 1);
    c.fillStyle = `rgba(6,4,9,${0.14 + irng() * 0.16})`;
    c.beginPath();
    c.ellipse(lx(p.x) + (irng() - 0.5) * 8, ly(p.y) + (irng() - 0.5) * 5, r, r * 0.55, irng() * Math.PI, 0, Math.PI * 2);
    c.fill();
  }
  if (v.family === 'rift' || v.family === 'crystal') {
    // broken ground chips thrown beside the split
    for (let i = 0; i < 5 && solid.length; i++) {
      const p = solid[Math.floor(irng() * solid.length)];
      const chx = lx(p.x) + (irng() - 0.5) * 16, chy = ly(p.y) + (irng() - 0.5) * 10;
      const s = 1.5 + irng() * 2.5;
      c.fillStyle = `rgba(196,178,150,${0.08 + irng() * 0.08})`;
      c.beginPath();
      c.moveTo(chx, chy - s); c.lineTo(chx + s, chy + s * 0.6); c.lineTo(chx - s * 0.9, chy + s * 0.5);
      c.fill();
    }
  }
  c.restore();
  // ── recessed dark fracture (23.27 shading kept: SW wall shadow, NE lit lip) ──
  trace(0, 0, '#0d0713', 4.2 + v.coreW * 0.9, 0.42);
  trace(-1.2, 1, 'rgba(0,0,0,0.45)', 1.4 + v.coreW * 0.45);
  trace(1.1, -0.9, 'rgba(196,178,150,0.13)', 1.2);
  trace(0, 0, '#160a20', 1.6 + v.coreW * 0.55, 1);
  // ── THE CRISP NEON CORE — baked, steady, never blurred (the pack's
  // hard law). Color temperature varies per cluster (anti-repetition). ──
  const hotR = Math.round(_lerp(205, 250, v.warm));
  const hotG = Math.round(_lerp(95, 110, v.warm));
  const hotB = Math.round(_lerp(255, 238, v.warm));
  trace(0, 0, 'rgba(130,35,255,0.85)', Math.max(1, v.coreW * 0.5 + 0.6));
  trace(0, 0, `rgba(${hotR},${hotG},${hotB},0.95)`, Math.max(0.6, v.coreW * 0.28));
  // ── crystal shards: dark facets with a neon rim, embedded flat ──
  for (const sh of v.shards) {
    c.save();
    c.beginPath();
    sh.pts.forEach((p, i) => { if (i === 0) c.moveTo(lx(p.x), ly(p.y)); else c.lineTo(lx(p.x), ly(p.y)); });
    c.closePath();
    c.fillStyle = 'rgba(16,7,26,0.92)';
    c.fill();
    c.strokeStyle = `rgba(${hotR},${hotG},${hotB},0.8)`;
    c.lineWidth = 0.9;
    c.stroke();
    // one inner facet line so it reads as cut crystal, not a blob
    if (sh.pts.length >= 4) {
      c.beginPath();
      c.moveTo(lx(sh.pts[0].x), ly(sh.pts[0].y));
      c.lineTo(lx(sh.pts[2].x), ly(sh.pts[2].y));
      c.strokeStyle = 'rgba(170,92,207,0.5)';
      c.lineWidth = 0.7;
      c.stroke();
    }
    c.restore();
  }
}

/** Bake ground integration + dark fracture + CRISP NEON CORE into a chunk. */
export function drawVeinBasesIntoChunk(
  c: CanvasRenderingContext2D,
  chunkX: number, chunkY: number, seed: number,
  lx: (wx: number) => number, ly: (wy: number) => number,
): void {
  for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
    for (const v of veinsForChunk(chunkX + dx, chunkY + dy, seed)) {
      drawVeinBase(c, v, seed, lx, ly);
    }
  }
}

// ── live soft glow (screen pass — the ONLY part that pulses) ────────────────
const GLOW_PX_PER_WU = 0.5;
const _glowCache = new Map<string, { cv: HTMLCanvasElement; ox: number; oy: number }>();

/** Per-vein soft-glow sprite (cached). Shared with the G8 dynamic cracks. */
export function veinGlowSprite(v: Vein): { cv: HTMLCanvasElement; ox: number; oy: number } | null {
  const hit = _glowCache.get(v.key);
  if (hit) return hit;
  if (_glowCache.size > 96) _glowCache.clear();
  const pad = Math.ceil(24 + v.glowR * 2);
  const w = Math.ceil((v.maxX - v.minX) * GLOW_PX_PER_WU) + pad * 2;
  const h = Math.ceil((v.maxY - v.minY) * GLOW_PX_PER_WU) + pad * 2;
  if (w <= 0 || h <= 0 || w > 1400 || h > 1400) return null;
  const cv = document.createElement('canvas');
  cv.width = w; cv.height = h;
  const c = cv.getContext('2d');
  if (!c) return null;
  const px = (wx: number) => (wx - v.minX) * GLOW_PX_PER_WU + pad;
  const py = (wy: number) => (wy - v.minY) * GLOW_PX_PER_WU + pad;
  const stroke = (color: string, width: number) => {
    c.strokeStyle = color;
    c.lineWidth = width;
    c.lineCap = 'round';
    c.lineJoin = 'round';
    c.beginPath();
    let up = true;
    for (const p of v.pts) {
      if (!p) { up = true; continue; }
      if (up) { c.moveTo(px(p.x), py(p.y)); up = false; }
      else c.lineTo(px(p.x), py(p.y));
    }
    c.stroke();
  };
  // soft aura ONLY — the crisp core is baked in the ground layer. Aura width
  // scales with family (hairline whisper → rift/crystal presence).
  stroke(`rgba(94,40,126,${0.16 + v.intensity * 0.14})`, v.glowR);
  stroke(`rgba(150,50,255,${0.14 + v.intensity * 0.10})`, v.glowR * 0.5);
  // branch-node radial lights — "small bright nodes at branch intersections"
  for (const n of v.nodes) {
    const nr = n.r * GLOW_PX_PER_WU * 2.2;
    const g = c.createRadialGradient(px(n.x), py(n.y), nr * 0.1, px(n.x), py(n.y), nr);
    g.addColorStop(0, `rgba(170,60,255,${0.30 + v.intensity * 0.25})`);
    g.addColorStop(0.6, 'rgba(150,50,255,0.12)');
    g.addColorStop(1, 'rgba(150,50,255,0)');
    c.fillStyle = g;
    c.beginPath();
    c.arc(px(n.x), py(n.y), nr, 0, Math.PI * 2);
    c.fill();
  }
  const out = { cv, ox: v.minX - pad / GLOW_PX_PER_WU, oy: v.minY - pad / GLOW_PX_PER_WU };
  _glowCache.set(v.key, out);
  return out;
}

/**
 * 23.27 — EERIE pulse personalities. Four styles seeded per vein, all SLOW
 * (3.2-7s clocks), each with its own unease. Returns the raw pulse alpha
 * (callers scale by intensity). Shared with the G8 dynamic cracks.
 */
export function veinPulseAlpha(v: Vein, nowMs: number): number {
  const styleH = (Math.imul(Math.round(v.minX), 0x9E3779B1) ^ Math.imul(Math.round(v.minY), 0x85EBCA6B)) >>> 0;
  const style = styleH % 4;
  const speed = 3.2 + (styleH % 97) / 97 * 3.8;           // 3.2-7s cycle
  const t = nowMs / 1000 / speed * Math.PI * 2 + v.phase;
  if (style === 0) {
    // deep slow breath
    return 0.20 + 0.30 * (0.5 + 0.5 * Math.sin(t));
  } else if (style === 1) {
    // heartbeat: two throbs, long rest
    const sfrac = (t / (Math.PI * 2)) % 1;
    const b1 = Math.exp(-Math.pow((sfrac - 0.10) / 0.045, 2));
    const b2 = Math.exp(-Math.pow((sfrac - 0.26) / 0.045, 2));
    return 0.14 + 0.42 * Math.max(b1, b2 * 0.75);
  } else if (style === 2) {
    // uneasy creep — two detuned slow waves wandering against each other
    return 0.18 + 0.10 * Math.sin(t * 0.9) + 0.10 * Math.sin(t * 2.13 + 1.7);
  }
  // slow wave that occasionally almost DIES out
  const dip = Math.max(0, Math.sin(t * 0.5 + 2.1));
  return (0.16 + 0.26 * (0.5 + 0.5 * Math.sin(t))) * (0.25 + 0.75 * Math.min(1, dip * 3));
}

/**
 * Draw all visible veins' pulsing glow. Call in WORLD space (inside the
 * camera transform), after the ground, before standing props.
 */
export function renderSurgeVeinGlow(
  ctx: CanvasRenderingContext2D,
  viewLeft: number, viewTop: number, viewRight: number, viewBottom: number,
  seed: number, nowMs: number,
): void {
  const c0x = Math.floor((viewLeft - 300) / CHUNK_WU), c1x = Math.floor((viewRight + 300) / CHUNK_WU);
  const c0y = Math.floor((viewTop - 300) / CHUNK_WU),  c1y = Math.floor((viewBottom + 300) / CHUNK_WU);
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  for (let cy = c0y; cy <= c1y; cy++) for (let cx = c0x; cx <= c1x; cx++) {
    for (const v of veinsForChunk(cx, cy, seed)) {
      if (v.maxX < viewLeft || v.minX > viewRight || v.maxY < viewTop || v.minY > viewBottom) continue;
      const g = veinGlowSprite(v);
      if (!g) continue;
      // hairlines whisper, epicenter families burn
      ctx.globalAlpha = Math.max(0.04, Math.min(1, veinPulseAlpha(v, nowMs) * (0.55 + v.intensity * 0.75)));
      ctx.drawImage(g.cv, g.ox, g.oy, g.cv.width / GLOW_PX_PER_WU, g.cv.height / GLOW_PX_PER_WU);
    }
  }
  ctx.restore();
}
